/**
 * Out of the ring (the one-bout format, user 2026-10-11; data/duelMatch.ts `DUEL_FORMAT`). In a v2 bout's fight the ring's
 * edge is a line, not a wall (duelMatch no longer holds fighters in): a fighter may walk, step, slide or be knocked out of
 * it. While out, their out-time runs; stepping back in resets it (user, 2026-10-11: each exit starts a fresh 10 s); one
 * continuous stay out longer than `outLimit` (10 s) loses the bout (duelMatch `forfeit`). Not while the bout is paused or between phases.
 * Deterministic: time only from `step(dt)`.
 */
import { DUEL_FLAGS, DUEL_FORMAT } from '../data/duelMatch';
import type { Entity } from '../world';
import { duelMatchOf } from './duelMatch';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelRingOutSystem>();
export const duelRingOutOf = (w: object): DuelRingOutSystem | null => SYSTEMS.get(w) ?? null;

/** Whether a fighter stands outside a ring (centre and radius in metres). */
export const outOfRing = (e: { x: number; y: number }, ring: { x: number; y: number; r: number }): boolean =>
  Math.hypot(e.x - ring.x, e.y - ring.y) > ring.r;

export class DuelRingOutSystem implements WorldSystem {
  readonly name = 'duelRingOut';
  /** Fighter -> seconds of its current stay out of the ring. */
  private readonly outT = new Map<number, number>();
  /** Fighters out of the ring at the last step. */
  private readonly out = new Set<number>();
  /** The bout this count belongs to (a new state object, or a new countdown, = a new bout). */
  private bout: object | null = null;
  private phase = '';
  /** The latest bout lost by being out (tests, the result card): the loser's id. */
  lastLoser: number | null = null;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  /** A fighter's out-of-ring state: out now, seconds of this stay out, seconds left before the loss. */
  infoOf(id: number): { out: boolean; t: number; left: number } {
    const t = this.outT.get(id) ?? 0;
    return { out: this.out.has(id), t, left: Math.max(0, DUEL_FORMAT.outLimit - t) };
  }

  step(dt: number): void {
    const w = this.h.world;
    const d = duelMatchOf(w);
    const st = d?.current ?? null;
    const fresh = st !== this.bout || (st?.phase === 'countdown' && this.phase !== 'countdown');
    this.phase = st?.phase ?? '';
    if (fresh) {
      this.bout = st;
      this.outT.clear();
      this.out.clear();
      if (st) this.lastLoser = null;
    }
    if (!d || !st || !DUEL_FLAGS.v2 || !DUEL_FORMAT.ringOut) return;
    if (st.phase !== 'fight' || st.paused || !w.pvpBout?.fight) {
      if (st.phase !== 'fight') this.out.clear();
      return;
    }
    const lost: Entity[] = [];
    for (const id of [st.meId, st.foeId]) {
      const e = w.get(id);
      if (!e || e.dead) continue;
      if (!outOfRing(e, st.ring)) {
        this.out.delete(id);
        this.outT.delete(id); // back in: the count starts afresh on the next exit
        continue;
      }
      this.out.add(id);
      const t = (this.outT.get(id) ?? 0) + dt;
      this.outT.set(id, t);
      if (t > DUEL_FORMAT.outLimit) lost.push(e);
    }
    if (!lost.length) return;
    // Both past the limit on one tick: the one out longer loses (equal: the challenger's side, `meId`, as the first listed).
    const loser = lost.length === 1 ? lost[0]! : (this.outT.get(lost[1]!.id)! > this.outT.get(lost[0]!.id)! ? lost[1]! : lost[0]!);
    this.lastLoser = loser.id;
    this.out.clear();
    d.forfeit(loser.id);
  }
}
